import React, { useState } from "react";
import { Info, RotateCcw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { MoneyInput } from "./MoneyInput";
import { rupees } from "../../../lib/auditMath";

export const SaveStatus = ({ id, record, retry, acceptSaved }) => {
  const r = record;
  if (!r?.status && !r?.conflict) return null;
  const current = r.conflict && (id === "annual-profit" ? r.conflict.annual_profit_inr : r.conflict.amount);
  return <div className={`audit-save ${r.status === "failed" ? "audit-error" : ""}`} data-testid={`audit-status-${id}`} role="status" aria-live="polite">
    {r.status === "saving" ? "Saving…" : r.status === "saved" ? "Saved ✓" : "Couldn't save, "}
    {r.status === "failed" && <button type="button" data-testid={`audit-retry-${id}`} onClick={() => retry(id)}><RotateCcw size={12} /> Retry</button>}
    {r.conflict && <div className="audit-conflict" data-testid={`audit-conflict-${id}`}>
      Changed elsewhere. Saved amount: {current == null ? "empty" : rupees(current)}.
      {r.conflict.choice && <span> Saved choice: {r.conflict.choice}.</span>}
      <button type="button" data-testid={`audit-use-saved-${id}`} onClick={() => acceptSaved(id)}>Use saved value</button>
    </div>}
  </div>;
};

const RuleInfo = ({ id, text }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      <button type="button" className="audit-info" aria-label="Projection rule explanation" data-testid={`audit-rule-info-${id}`}
        onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        onFocus={(e) => { if (e.currentTarget.matches(":focus-visible")) setOpen(true); }}
        onBlur={() => setOpen(false)}><Info size={15} /></button>
    </PopoverTrigger>
    <PopoverContent data-testid={`audit-rule-tooltip-${id}`} className="audit-tooltip" side="top" align="end"
      onOpenAutoFocus={(e) => e.preventDefault()} onMouseLeave={() => setOpen(false)}>{text}</PopoverContent>
  </Popover>;
};

export const AuditRow = ({ rule, result, rules, saveState, readOnly = false }) => {
  const { records, change, save, retry, acceptSaved } = saveState;
  const r = records[rule.id];
  const locked = <span className="audit-locked">{readOnly ? "—" : "Enter your cost to unlock"}</span>;
  return <tr data-testid={`audit-row-${rule.id}`}>
    <td><span className="audit-mobile-label">Gap found</span><strong data-testid={`audit-name-${rule.id}`}>{rule.name}</strong></td>
    <td><span className="audit-mobile-label">Cost formula</span><span className="audit-formula" data-testid={`audit-formula-${rule.id}`}>{rule.formula}</span></td>
    <td>
      <span className="audit-mobile-label">What it costs me (₹/month)</span>
      <MoneyInput id={`audit-cost-${rule.id}`} value={r.amount} label={`${rule.name}: What it costs me (₹/month)`} disabled={readOnly}
        onChange={(v) => change(rule.id, "amount", v)} onBlur={() => save(rule.id)} />
      {!readOnly && <SaveStatus id={rule.id} record={r} retry={retry} acceptSaved={acceptSaved} />}
    </td>
    <td data-testid={`audit-implement-${rule.id}`}><span className="audit-mobile-label">What I need to implement</span>
      {!result.unlocked ? locked : rule.options ? <fieldset aria-label="Implementation choice" className="audit-choices">
        {Object.entries(rule.options).map(([key, label]) => <label key={key}>
          <input type="radio" name={`choice-${rule.id}`} value={key} checked={r.choice === key} data-testid={`audit-choice-${key}`} disabled={readOnly}
            onChange={() => change(rule.id, "choice", key)} /><span>{label}</span>
        </label>)}
        {!r.choice && !readOnly && <p className="audit-hint" data-testid="audit-choice-hint">Choose one option</p>}
      </fieldset> : rule.implement}
    </td>
    <td data-testid={`audit-growth-${rule.id}`}><span className="audit-mobile-label">Growth it unlocks (₹/month)</span>
      {!result.unlocked ? locked : <>
        {result.growth !== null && <>
          <div className="audit-rule" data-testid={`audit-rule-${rule.id}`}>Cost × {rule.multiplier}
            <RuleInfo id={rule.id} text={rules[String(rule.multiplier)]} />
          </div>
          <strong className="audit-result" data-testid={`audit-result-${rule.id}`}>{rupees(result.growth)} <span>/month</span></strong>
        </>}
      </>}
    </td>
  </tr>;
};